/**
 * Utility functions for handling YouTube videos
 *
 * UPDATED: Added chunked cache support via manifest.json.
 * Fetch priority: manifest → category JSONs → api/videos.php → cached_videos.json
 */

import { API_URL, FALLBACK_URLS, MANIFEST_URL, PUBLIC_FLAGS_URL } from '../constants.js';

/**
 * Generates a YouTube embed URL from a video ID
 * @param {string} videoId - The YouTube video ID
 * @returns {string} The full YouTube embed URL
 */
export const resolveVideoId = (videoId) => {
  if (!videoId) {
    return '';
  }
  if (typeof videoId === 'string') {
    return videoId;
  }
  if (typeof videoId === 'object' && videoId !== null) {
    if (
      typeof videoId.id === 'object' &&
      videoId.id !== null &&
      typeof videoId.id.videoId === 'string'
    ) {
      return videoId.id.videoId;
    }
    const resolved = videoId.videoId || videoId.id;
    return typeof resolved === 'string' ? resolved : '';
  }
  return '';
};

export const getYouTubeEmbedUrl = (videoId) => {
  const id = resolveVideoId(videoId);
  return id ? `https://www.youtube-nocookie.com/embed/${id}?rel=0&modestbranding=1` : '';
};

/**
 * Gets a thumbnail URL or fallback to placeholder
 * @param {object} video - Video object
 * @returns {string} URL to thumbnail or placeholder
 */
export const getThumbnailUrl = (video) => {
  const url =
    video?.snippet?.thumbnails?.high?.url ||
    video?.snippet?.thumbnails?.medium?.url ||
    video?.thumbnail ||
    video?.thumbnail_url ||
    video?.thumbnailUrl;
  return url || 'placeholder-image.jpg';
};

const extractVideos = (categoryData) => {
  if (Array.isArray(categoryData)) {
    return categoryData;
  }
  if (categoryData && Array.isArray(categoryData.videos)) {
    return categoryData.videos;
  }
  return [];
};

const applyFilteredVideos = (categoryData, filteredVideos) => {
  if (Array.isArray(categoryData)) {
    return filteredVideos;
  }
  if (!categoryData) {
    return filteredVideos;
  }
  return { ...categoryData, videos: filteredVideos };
};

const isExplicitValue = (value, expected) => {
  if (value === null || value === undefined) {
    return false;
  }
  if (typeof value === 'boolean') {
    return value === Boolean(expected);
  }
  if (typeof value === 'number') {
    return value === expected;
  }
  if (typeof value === 'string') {
    return value.trim() === String(expected);
  }
  return false;
};

const shouldExcludeVideo = (video, videoFlags, blockedChannelIds) => {
  const status = video?.status || {};

  if (isExplicitValue(video?.status_embeddable, 0) || isExplicitValue(status?.embeddable, 0)) {
    return true;
  }
  if (
    isExplicitValue(video?.status_selfdeclaredmadeforkids, 1) ||
    isExplicitValue(status?.selfDeclaredMadeForKids, 1)
  ) {
    return true;
  }
  if (isExplicitValue(video?.status_madeforkids, 1) || isExplicitValue(status?.madeForKids, 1)) {
    return true;
  }
  if (isExplicitValue(video?.channelinfo_status_madeforkids, 1)) {
    return true;
  }
  if (isExplicitValue(video?.isnaughty, 1)) {
    return true;
  }
  if (
    isExplicitValue(video?.status_containssyntheticmedia, 1) ||
    isExplicitValue(video?.status_containsSyntheticMedia, 1) ||
    isExplicitValue(status?.containsSyntheticMedia, 1)
  ) {
    return true;
  }
  if (isExplicitValue(video?.notasmr, 1)) {
    return true;
  }

  // Admin-set flags from flagged_videos.json (fetched via public-flags.php)
  if (videoFlags) {
    if (isExplicitValue(videoFlags.isnaughty, 1)) return true;
    if (isExplicitValue(videoFlags.notasmr, 1)) return true;
    if (isExplicitValue(videoFlags.status_embeddable, 0)) return true;
    if (isExplicitValue(videoFlags.status_madeforkids, 1)) return true;
    if (isExplicitValue(videoFlags.status_selfdeclaredmadeforkids, 1)) return true;
  }

  // Channel block check from blocked_channels.json (fetched via public-flags.php)
  if (blockedChannelIds && blockedChannelIds.size > 0) {
    const channelId = video?.snippet?.channelId || video?.snippet?.channelid || '';
    if (channelId && blockedChannelIds.has(channelId)) {
      return true;
    }
  }

  return false;
};

export const sanitizeCategorizedVideos = (rawData, adminFlags) => {
  if (!rawData || typeof rawData !== 'object') {
    return {};
  }

  const flaggedVideos = adminFlags?.flaggedVideos || {};
  const blockedChannelIds = new Set(Object.keys(adminFlags?.blockedChannels || {}));

  const normalized = rawData.videos && typeof rawData.videos === 'object'
    ? rawData.videos
    : rawData;

  const sanitized = {};

  Object.keys(normalized).forEach((category) => {
    if (!Object.prototype.hasOwnProperty.call(normalized, category)) {
      return;
    }

    const categoryData = normalized[category];
    const videos = extractVideos(categoryData);

    if (!Array.isArray(videos) || videos.length === 0) {
      return;
    }

    const filteredVideos = videos.filter((video) => {
      const videoId = typeof video?.id === 'string' ? video.id : '';
      const videoFlags = videoId ? flaggedVideos[videoId] : undefined;
      return !shouldExcludeVideo(video, videoFlags, blockedChannelIds);
    });

    if (filteredVideos.length === 0) {
      return;
    }

    sanitized[category] = applyFilteredVideos(categoryData, filteredVideos);
  });

  return sanitized;
};

const fetchJson = async (url) => {
  const response = await fetch(url);
  const contentType = response.headers.get('content-type') || '';
  if (!response.ok) {
    const bodyText = await response.text();
    if (bodyText) {
      const truncatedBody = bodyText.length > 200 ? `${bodyText.substring(0, 200)}...` : bodyText;
      console.error('fetchCategorizedVideos: Error response body from', url, ':', truncatedBody);
    }
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  let parsed;
  try {
    parsed = await response.json();
  } catch (error) {
    console.error('fetchCategorizedVideos: Failed to parse JSON from', url, ':', error);
    if (!contentType.includes('application/json')) {
      throw new Error('Response is not JSON');
    }
    throw new Error('Failed to parse JSON response');
  }

  if (!contentType.includes('application/json')) {
    console.warn('fetchCategorizedVideos: Received non-JSON content-type from', url, 'but JSON parsing succeeded.');
  }

  if (parsed && typeof parsed === 'object' && parsed.error) {
    throw new Error(parsed.error);
  }

  return parsed;
};

// ============================================================
// NEW: Chunked cache loader (reads manifest, then category files)
// ============================================================

/**
 * Tries to load videos from the chunked cache system.
 * Reads manifest.json, then fetches all category files in parallel.
 * Returns data shaped the same as api/videos.php (categorized object).
 * 
 * @returns {Promise<Object|null>} Categorized video data, or null if unavailable.
 */
async function fetchFromChunkedCache() {
  try {
    const manifest = await fetchJson(MANIFEST_URL);
    if (!manifest || !manifest.version || !manifest.categories) {
      return null;
    }

    const version = manifest.version;
    const categories = manifest.categories;

    // Fetch all category files in parallel
    const categoryPromises = categories.map(async (catName) => {
      try {
        const url = `script/cache/${version}/by-category/${catName}.json`;
        const data = await fetchJson(url);
        return { name: catName, data };
      } catch (err) {
        console.warn(`Chunked cache: Failed to load category ${catName}:`, err);
        return { name: catName, data: null };
      }
    });

    const results = await Promise.all(categoryPromises);

    // Build categorized object matching the shape api/videos.php returns
    const categorized = {};
    for (const { name, data } of results) {
      if (data && data.videos && data.videos.length > 0) {
        categorized[name] = {
          title: data.category || name,
          videos: data.videos
        };
      }
    }

    if (Object.keys(categorized).length === 0) {
      return null;
    }

    console.log(`Chunked cache: Loaded ${Object.keys(categorized).length} categories from ${version}`);
    return categorized;
  } catch (err) {
    console.warn('Chunked cache: Manifest not available, falling back.', err);
    return null;
  }
}

// ============================================================
// PUBLIC API
// ============================================================

/**
 * Fetches admin-set video flags and blocked channels from the public read-only endpoint.
 * Fails silently — returns null if the endpoint is unavailable.
 *
 * @returns {Promise<{flaggedVideos: Object, blockedChannels: Object}|null>}
 */
async function fetchAdminFlags() {
  try {
    const data = await fetchJson(PUBLIC_FLAGS_URL);
    if (!data || typeof data !== 'object') return null;
    return {
      flaggedVideos: data.flagged_videos || {},
      blockedChannels: data.blocked_channels || {},
    };
  } catch {
    return null;
  }
}

/**
 * Fetches categorized videos from cached data.
 * Priority: chunked cache → api/videos.php → cached_videos.json fallbacks
 * Admin flags (flagged videos + blocked channels) are fetched concurrently and
 * applied during sanitization so banned content is hidden on the index page.
 *
 * @returns {Promise<Object>} Promise that resolves to categorized video object
 */
export async function fetchCategorizedVideos() {
  // Kick off admin flags fetch concurrently — fail-safe, never delays video data
  const adminFlagsPromise = fetchAdminFlags();

  let rawData = null;
  let primaryError = null;

  // 1. Try chunked cache (static JSON on CDN — fastest, free)
  try {
    const chunkedData = await fetchFromChunkedCache();
    if (chunkedData) {
      rawData = chunkedData;
    }
  } catch (chunkedError) {
    console.warn('fetchCategorizedVideos: Chunked cache failed:', chunkedError);
  }

  // 2. Try PHP API (reads from chunked cache server-side, or falls back to legacy)
  if (!rawData) {
    try {
      rawData = await fetchJson(API_URL);
    } catch (err) {
      primaryError = err;
      console.error('fetchCategorizedVideos: Primary source failed:', err);

      // 3. Try legacy JSON files concurrently
      try {
        rawData = await Promise.any(
          FALLBACK_URLS.map(async (fallbackUrl) => {
            const data = await fetchJson(fallbackUrl);
            console.warn(`fetchCategorizedVideos: Using fallback data from ${fallbackUrl}`);
            return data;
          })
        );
      } catch (aggregateError) {
        console.error('fetchCategorizedVideos: All fallback sources failed.');
        throw primaryError;
      }
    }
  }

  const adminFlags = await adminFlagsPromise;
  return sanitizeCategorizedVideos(rawData, adminFlags);
}
